import RequestMethod from "../models/client/RequestMethod";
import RestResponse, {
  ErrorResponseJson,
  RestErrorResponse,
  RestSuccessResponse,
  responseFromClientAuthFailure,
  responseFromError,
} from "../models/client/RestResponse";
import { getEnabledFeatures } from "../stores/enabled-features.store";
import { logger } from "@/lib/logger";
import { isAbortError, isCancellation } from "@/lib/abort";
import { isInteractiveAuthError } from "@/lib/auth/authRecovery";

type Body =
  | Record<string, unknown>
  | { [p: string]: string | unknown }
  | unknown
  | BodyInit;

type Fetch = <T>(
  method: RequestMethod,
  path: string,
  body?: Body,
  queryParameters?: Array<undefined | object>,
  headers?: Record<string, string>,
  isFile?: boolean,
  options?: { signal?: AbortSignal; cache?: RequestCache }
) => RestResponse<T>;

type FetchAsBlob = (
  method: RequestMethod,
  path: string,
  queryParameters?: Array<undefined | object>,
  headers?: Record<string, string>
) => RestResponse<Blob>;

export type RestClient = {
  host: string;
  fetch: Fetch;
  fetchAsBlob: FetchAsBlob;
};

function buildSearchParams(
  queryParameters?: Array<undefined | object>
): string {
  if (!queryParameters) return "";
  return (
    "?" +
    queryParameters
      .filter(
        (queryParameter) =>
          queryParameter !== undefined &&
          Object.keys(queryParameter).length !== 0
      )
      .map((queryParameter) => {
        if (queryParameter) {
          return Object.entries(queryParameter)
            .filter(([key, value]) => value !== undefined && key)
            .map(([key, value]) => `${key}=${value}`)
            .join("&");
        }
        return "";
      })
      .join("&")
  );
}

function buildRequestHeaders(options: {
  authorization: string;
  accept: string;
  contentType?: string;
  extra?: Record<string, string>;
}): HeadersInit {
  const enabledFeatures = getEnabledFeatures();
  return {
    Accept: options.accept,
    "Accept-Encoding": "gzip, deflate, br",
    Connection: "keep-alive",
    "Access-Control-Allow-Credentials": "true",
    ...(options.contentType && { "Content-Type": options.contentType }),
    ...(enabledFeatures.length > 0 && {
      "X-Enabled-Features": enabledFeatures.join(","),
    }),
    ...(options.extra ?? {}),
    Authorization: options.authorization,
  };
}

const DEFAULT_FETCH_OPTIONS: RequestInit = {
  mode: "cors",
  credentials: "same-origin",
  redirect: "follow",
  referrerPolicy: "no-referrer",
};

function successResponse<T>(
  value: T,
  status: number,
  statusText: string
): RestSuccessResponse<T> {
  return {
    success: true,
    status,
    statusText,
    feedback: { severity: "success", message: "" },
    value,
  };
}

/**
 * The abort a body read rejected with, as an `AbortError`: a signal aborted with a custom reason, or
 * a read that failed some other way once the signal had aborted, still reads as a cancellation.
 */
const abortErrorFrom = (error: unknown): unknown =>
  isAbortError(error)
    ? error
    : new DOMException("The request was aborted", "AbortError");

/**
 * Reads a `fetch` response body: parsed JSON when the server says it is JSON, text otherwise, null
 * when it is empty.
 *
 * An abort can land after the headers arrived, while the body is still streaming. The read then
 * rejects, and it must surface as the cancellation it is: it used to read as a successful response
 * with a null body, and callers iterating `value` threw "s.value is not iterable" (LAR-585).
 *
 * Without an abort, nothing changes for callers: a JSON body that cannot be read or parsed still
 * reads as null (it is now logged), and a text body that cannot be read still throws.
 */
const readBody = async (
  response: Response,
  path: string,
  signal: AbortSignal | undefined
): Promise<unknown> => {
  const contentLength = response.headers.get("content-length");
  if (contentLength !== null && Number(contentLength) === 0) return null;

  const isJson = response.headers
    .get("Content-Type")
    ?.includes("application/json");

  const unreadableJson = (error: unknown) => {
    logger.warn("API response body is not readable JSON; reading it as null", {
      operation: "restClientParseBody",
      error,
      path,
      status: response.status,
    });
    return null;
  };

  let text: string;
  try {
    text = await response.text();
  } catch (error) {
    if (isCancellation(error, signal)) throw abortErrorFrom(error);
    if (isJson) return unreadableJson(error);
    throw error;
  }

  if (!isJson) return text;
  if (text.length === 0) return null;

  try {
    return JSON.parse(text);
  } catch (error) {
    return unreadableJson(error);
  }
};

type ExecuteRequestParams = {
  method: RequestMethod;
  path: string;
  queryParameters?: Array<undefined | object>;
  headers?: Record<string, string>;
  accept: string;
  contentType?: string;
  body?: BodyInit | null;
  cache?: RequestCache;
  signal?: AbortSignal;
};

export const restClientAuth0 = (
  host: string,
  getTokenSilently: () => Promise<string>,
  isAuthenticated: () => boolean
): RestClient => {
  const guardAuth = (path: string): RestErrorResponse | null => {
    if (!isAuthenticated()) {
      // The SDK can drop `isAuthenticated` on a live session (silent auth
      // answering login_required makes it call logout({openUrl:false})), so this
      // is a recoverable blip, not a sign-out. Logged through `logger` so the
      // rate is queryable — it used to be console-only (LAR-361).
      logger.warn("Attempted API call while not authenticated", {
        operation: "restClientGuardAuth",
        path,
      });
      return responseFromClientAuthFailure(
        "not-authenticated",
        "User is not authenticated"
      );
    }
    return null;
  };

  const getAuthorization = async (): Promise<string> => {
    const token = await getTokenSilently();
    return `Bearer ${token}`;
  };

  const executeRequest = async (
    params: ExecuteRequestParams
  ): Promise<RestErrorResponse | { success: true; response: Response }> => {
    const authError = guardAuth(params.path);
    if (authError) return authError;

    const url = `${host}${params.path}${buildSearchParams(params.queryParameters)}`;
    let authorization: string;
    try {
      authorization = await getAuthorization();
    } catch (e) {
      // A failed token fetch here is the refresh-token rotation breach surfacing
      // on an API call; log it so it is queryable in App Insights (LAR-290).
      // Not when it needs an interactive login: the token getter has already
      // redirected to Auth0 and logged that once (`recoverFromInteractiveAuthError`),
      // and every concurrent request fails with the same error (LAR-597).
      if (!isInteractiveAuthError(e)) {
        logger.error("Failed to retrieve authentication token", {
          operation: "restClientGetToken",
          error: e,
          path: params.path,
        });
      }
      return responseFromClientAuthFailure(
        "token-unavailable",
        "Failed to retrieve authentication token"
      );
    }

    const constructedHeaders = buildRequestHeaders({
      authorization,
      accept: params.accept,
      contentType: params.contentType,
      extra: params.headers ?? {},
    });

    let response: Response;
    try {
      response = await fetch(url, {
        ...DEFAULT_FETCH_OPTIONS,
        method: RequestMethod[params.method],
        cache: params.cache ?? "no-cache",
        headers: constructedHeaders,
        body: params.body ?? undefined,
        signal: params.signal,
      });
    } catch (error) {
      // React Query aborts in-flight requests on remount; do not treat as network failure.
      if (isAbortError(error)) {
        throw error;
      }
      // No response at all: offline, DNS, CORS or TLS. It stays a 408 so the retry policies keep
      // treating it as transient (`lib/query/retry.ts`, `lib/queryRetry.ts`), but the 408 cannot
      // say which of those it was, so the browser's own error is logged here (LAR-627).
      if (!isCancellation(error, params.signal)) {
        logger.warn("API request failed before a response arrived", {
          operation: "restClientFetch",
          error,
          method: RequestMethod[params.method],
          path: params.path,
        });
      }
      return responseFromError(408);
    }
    return { success: true as const, response };
  };

  return {
    host,

    fetch: async <T>(
      method: RequestMethod,
      path: string,
      body?: Body,
      queryParameters?: Array<undefined | object>,
      headers: Record<string, string> = {},
      isFile?: boolean,
      options: { signal?: AbortSignal; cache?: RequestCache } = {}
    ) => {
      const result = await executeRequest({
        method,
        path,
        queryParameters,
        headers,
        accept: "application/json",
        contentType: !isFile ? "application/json" : undefined,
        body: isFile ? (body as BodyInit) : (JSON.stringify(body) as BodyInit),
        cache: options.cache,
        signal: options.signal,
      });

      if (!result.success) return result;

      const { response } = result;
      const responseBody = await readBody(response, path, options.signal);

      if (!response.ok)
        return responseFromError(
          response.status,
          responseBody as ErrorResponseJson | string
        );

      return successResponse(
        responseBody as T,
        response.status,
        response.statusText
      ) as RestSuccessResponse<T>;
    },

    fetchAsBlob: async (
      method: RequestMethod,
      path: string,
      queryParameters?: Array<undefined | object>,
      headers: Record<string, string> = {}
    ) => {
      const result = await executeRequest({
        method,
        path,
        queryParameters,
        headers,
        accept: "application/pdf",
        cache: "no-cache",
      });

      if (!result.success) return result;

      const { response } = result;
      if (!response.ok)
        return responseFromError(response.status, await response.text());

      const blob = await response.blob();
      return successResponse(
        blob,
        response.status,
        response.statusText
      ) as RestSuccessResponse<Blob>;
    },
  };
};
